import {
  type SampleAssetRepository,
  type CaseRepository,
  type SampleRepository,
  UuidGenerator,
  type WorkspaceRepository,
} from '@telespot/domain';
import { type CommandHandler } from '@telespot/core';
import { CopyCases } from './copy-cases.command';

/**
 * Handler interface for the {@link CopyCases} command
 *
 * @group Workspaces
 * @category Handlers
 */
export type ICopyCasesHandler = CommandHandler<CopyCases>;

/**
 * Default implementation of the {@link ICopyCasesHandler}
 *
 * @group Workspaces
 * @category Handlers
 */
export class CopyCasesHandler implements ICopyCasesHandler {
  constructor(
    private readonly workspaces: WorkspaceRepository,
    private readonly cases: CaseRepository,
    private readonly sampleAssets: SampleAssetRepository,
    private readonly samples: SampleRepository,
    private readonly uuids: UuidGenerator
  ) {}

  /**
   * Executes the CopyCasesFromWS
   * @param command with the minimum details needed
   * @returns once the process of copy has been completed
   */
  public async execute(command: CopyCases): Promise<void> {
    const { workspaceId, caseId } = command.details;

    // Fetch the needed objects to start the process
    const subjectCase = await this.cases.getById(caseId);
    const destWS = await this.workspaces.getById(workspaceId);

    // Create a new Case (replica) in the selected Workspace (destWS)
    const copiedCase = subjectCase.copyToWorkspace(destWS, this.uuids.generate());

    const newCase = await this.cases.save(copiedCase);

    // Create the Samples Query for the selected Case
    const sampleBatches = this.samples.findInCase(subjectCase.id);

    // Iterate over every Sample from the selected Case using the generator function to allow scalability (results > 100)
    for await (const samples of sampleBatches) {
      const sampleIds = samples.map((s) => s.id);

      // Create the new Samples (replica) with the corresponded new Case
      const copiedSamples = samples.map((sample) => sample.copyToCase(newCase.id, this.uuids.generate()));

      const newSamples = await this.samples.saveAll(...copiedSamples);

      // Create the SampleAsset Query for each Sample
      const sampleAssets = this.sampleAssets.forEachInSample(...sampleIds);

      const copiedSampleAssets = [];

      // Iterate over every relation for the Sample using the generator function to allow scalability (results > 100)
      for await (const sampleAsset of sampleAssets) {
        const oldSampleIndex = samples.findIndex((s) => s.id === sampleAsset.sampleId);
        const newSample = newSamples.find((_, i) => oldSampleIndex === i);

        // Create the new relations between the new Samples and the correspondent Asset
        copiedSampleAssets.push(sampleAsset.copyToSample(newSample.id, this.uuids.generate()));
      }

      await this.sampleAssets.saveAll(...copiedSampleAssets);
    }
  }
}
